import React, { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  Card,
  CardHeader,
  Typography,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useSentMessages } from './hooks/useSentMessages';
import SentMessagesFilters from '../../sentMessages/components/SentMessagesFilters';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';

dayjs.extend(customParseFormat);

const CANAL_COLORS = {
  WhatsApp: 'success',
  SMS: 'warning',
  Email: 'info',
};

export default function MessageSentPage() {
  const [groupByTenant, setGroupByTenant] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const startDate = useMemo(() => selectedMonth.startOf('month'), [selectedMonth]);
  const endDate = useMemo(() => selectedMonth.endOf('month'), [selectedMonth]);

  const { messages: allMessages, isLoading, error } = useSentMessages();

  // Filtro por mês feito no front, pela data de envio (sentAt vem como DD/MM/YYYY HH:mm:ss).
  const monthMessages = useMemo(
    () =>
      allMessages.filter((message) => {
        const sentAt = dayjs(message.sentAt, 'DD/MM/YYYY HH:mm:ss');
        return !sentAt.isBefore(startDate) && !sentAt.isAfter(endDate);
      }),
    [allMessages, startDate, endDate],
  );

  // O endpoint agrupado só traz contagens, sem data, então o agrupamento também é feito aqui.
  const messages = useMemo(() => {
    if (!groupByTenant) return monthMessages;
    const groups = new Map();
    monthMessages.forEach((message) => {
      const key = `${message.tenantId}|${message.channel}`;
      const group = groups.get(key) ?? {
        tenant: message.tenantId,
        channel: message.channel,
        messagesQuantity: 0,
      };
      group.messagesQuantity += 1;
      groups.set(key, group);
    });
    return [...groups.values()];
  }, [monthMessages, groupByTenant]);

  const changeSelectedMonth = useCallback(
    (date) => date && setSelectedMonth(date.startOf('month')),
    [],
  );

  useEffect(() => {
    if (error) {
      setShowErrorMessage(true);
      setErrorMessage('Não foi possível carregar as mensagens enviadas.');
    }
  }, [error]);

  return (
    <>
      <Loading isToHidden={!isLoading} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Mensagens Enviadas
            </Typography>
          }
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={groupByTenant}
                  onChange={(e) => setGroupByTenant(e.target.checked)}
                />
              }
              label="Agrupar por Tenant"
              sx={{ mr: 1 }}
            />
          }
        />

        <CardContent>
          <Stack spacing={3}>
            <SentMessagesFilters
              selectedMonth={selectedMonth}
              onChangeMonth={changeSelectedMonth}
            />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Canal</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tenant</TableCell>
                    {!groupByTenant && (
                      <TableCell sx={{ fontWeight: 'bold' }}>Destinatário</TableCell>
                    )}
                    {groupByTenant ? (
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantidade de Mensagens</TableCell>
                    ) : (
                      <TableCell sx={{ fontWeight: 'bold' }}>Data do Envio</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.length === 0 && !isLoading ? (
                    <TableRow>
                      <TableCell colSpan={groupByTenant ? 3 : 4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">Nenhuma mensagem encontrada.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    messages.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip
                            label={row.channel}
                            color={CANAL_COLORS[row.channel] ?? 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{groupByTenant ? row.tenant : row.tenantId}</TableCell>
                        {!groupByTenant && <TableCell>{row.recipient}</TableCell>}
                        <TableCell>{groupByTenant ? row.messagesQuantity : row.sentAt}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
